import React, {useState} from 'react';
import {CircularProgress, makeStyles, Typography} from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";

import MobileLogoLight from '../../assets/fixably-logo.svg';
import MobileLogoDark from '../../assets/fixably-logo-darkmode.svg';
import clsx from "clsx";
import Button from "@material-ui/core/Button";
import {styles} from "../../theme/theme";
import {connect} from "react-redux";
import {loginRequest} from "../../redux/actions/applicationData";
import {useTheme} from "@material-ui/core/styles";

import Configuration from "../../configuration";

import {getEnvironmentVariables} from "../../configuration/environment";
import {readStoredIdentifier} from "../../services/FirebaseService";
import {CURRENT_VERSION} from "../../services/PWAUpdater";

const useStyles = makeStyles(theme => ({
    highlightButton: {
        background:
            theme.palette.type === 'light'
                ? `linear-gradient(90deg, ${styles.colors.fixablyPrimary} 0%, ${styles.colors.fixablyPrimary} 25%, ${styles.colors.fixablyWhite} 50%, ${styles.colors.fixablyPrimary} 75%, ${styles.colors.fixablyPrimary} 100%)`
                : `linear-gradient(90deg, ${styles.colors.fixablyPrimary} 0%, ${styles.colors.fixablyPrimary} 25%, ${styles.colors.fixablyWhite} 50%, ${styles.colors.fixablyPrimary} 75%, ${styles.colors.fixablyPrimary} 100%)`,
        backgroundSize: '200%',
        backgroundPosition: '0% 0%',
        transition: 'all 250ms ease',
        border: 0,
        borderRadius: 24,
        color: 'white',
        height: 48,
        width: '100%',
        '&:hover': {
            backgroundPosition: '100% 0%',
            transition: 'all 250ms ease',
        },
    },
    logoMobile: {
        // Quoted, and it has to be. Vite inlines an svg under assetsInlineLimit
        // (4096; these are 2064) as data:image/svg+xml,... with its attribute
        // quotes left as literal single quotes. CSS does not allow a quote
        // character inside an unquoted url() token, so the whole declaration is
        // discarded and no logo renders. Vite encodes the payload with single
        // quotes precisely so that wrapping it in double quotes is safe.
        backgroundImage: `url("${MobileLogoLight}")`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        margin: theme.spacing(1),
        marginBottom: theme.spacing(5),
        height: 60,
        width: 200,
    },
    container: {
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    paper: {
        padding: theme.spacing(4),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    returning: {
        // Wide enough to read, narrow enough that the identifier wraps rather
        // than stretching the card on a tablet.
        maxWidth: 320,
        marginBottom: theme.spacing(3),
        textAlign: 'center',
    },
    identifier: {
        // Support reads this back over the phone, so it must not be mistaken
        // for prose or lose a character to a ligature.
        fontFamily: 'monospace',
        wordBreak: 'break-all',
    },
    margin: {
        margin: theme.spacing(1),
    },
    marginBottom: {
        marginBottom: theme.spacing(6),
    },
    textField: {
        width: '100%',
    },
    cardHeader: {
        backgroundColor: theme.palette.type === 'light' ? theme.palette.primary.main : theme.palette.primary.dark,
        color: theme.palette.type === 'light' ? theme.palette.getContrastText(theme.palette.primary.main) : theme.palette.getContrastText(theme.palette.primary.dark),
    },
    leftIcon: {
        marginRight: theme.spacing(1),
    },
    rightIcon: {
        marginLeft: theme.spacing(1),
    },
    iconSmall: {
        fontSize: 20,
    },
}));

let loginTimeoutHandle = null;

export function Login(props) {

    const {
        loginRequest,
    } = props;

    const authURL = getEnvironmentVariables().auth.baseURL;
    const redirectURL = getEnvironmentVariables().auth.redirectURL;
    const scopes = [
        'user:view',
        'signature:view',
        'signature:sign',
        'signature_terminal:view',
        'signature_terminal:register',
        'order:upload_file',
    ];
    const queryParams = [
        'response_type=code',
        `client_id=${Configuration.api.clientId}`,
        `redirect_uri=${redirectURL}`,
        `scope=${scopes.join(' ')}`
    ];
    const loginUrl = `${authURL}?${queryParams.join('&')}`;

    const theme = useTheme();
    const mobileLogo = theme.palette.type === 'light' ? MobileLogoLight : MobileLogoDark;

    const classes = useStyles();

    const [loggingIn, setLoggingIn] = useState(false);

    // Read once on mount. Nothing writes the identifier while this screen is up,
    // and re-reading on every render would hit storage for no reason.
    const [storedIdentifier] = useState(readStoredIdentifier);

    function endLoggingIn() {
        setLoggingIn(false);
    }

    if (loginTimeoutHandle === null) {
        const query = window.location.search.substring(1);
        const vars = query.split('&');
        for (let i = 0; i < vars.length; i++) {
            const pair = vars[i].split('=');
            if (decodeURIComponent(pair[0]) === 'code') {
                const code = decodeURIComponent(pair[1]);
                loginTimeoutHandle = setTimeout(() => {
                    setLoggingIn(true);
                    loginRequest({code: code, doAfter: () => endLoggingIn()});
                }, 0);
            }
        }
    }

    return (
        <Box style={ {height: '100vh', width: '100%', display: 'flex', flexDirection: 'column',} }>
            <Box className={classes.container}>
                <Paper className={classes.paper} elevation={4}>
                    <Box data-testid="login-logo" className={classes.logoMobile} style={{backgroundImage: `url("${mobileLogo}")`}}/>
                    {loggingIn ?
                        <CircularProgress color='primary'/> :
                        <>
                            {storedIdentifier &&
                                <Box className={classes.returning}>
                                    <Typography variant={'subtitle1'}>Signing in again</Typography>
                                    {/*
                                        Deliberately does NOT claim the pairing survived. A terminal
                                        removed in Fixably still has both storage keys, so "you are
                                        still paired" would be a confident lie to exactly the
                                        customer who needs the truth. "Does not set up a new
                                        terminal" is true in every case, and it is the sentence that
                                        answers what staff actually misread: Core's consent screen
                                        lists "Register signature terminal", which reads like setup.
                                        Actual pairing state is reported after login by
                                        DeviceInformation.
                                    */}
                                    <Typography variant={'body2'} color={'textSecondary'}>
                                        This tablet is already registered as{' '}
                                        <span className={classes.identifier}>{storedIdentifier}</span>.
                                        Signing in does not set up a new terminal.
                                    </Typography>
                                </Box>}
                            <Button
                                href={loginUrl}
                                className={clsx(classes.highlightButton)}>Login</Button>
                        </>}
                </Paper>
            </Box>
            <Box style={{width: '100%', textAlign: 'right', paddingRight: 6 }}><Typography color={'textSecondary'} variant={'overline'}>v. {CURRENT_VERSION}</Typography></Box>
        </Box>
    );
}

export default connect(null, {
    loginRequest,
})(Login);
